import { useEffect } from "react";
import Lenis from "lenis";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { ContentProvider, useContent } from "@/context/ContentContext";
import { Navbar, scrollTo } from "@/components/Navbar";
import { Hero } from "@/components/Hero";
import { EditorialMarquee } from "@/components/Marquee";
import { Manifesto } from "@/components/Manifesto";
import { Services } from "@/components/Services";
import { HowItWorks } from "@/components/HowItWorks";
import { Packages } from "@/components/Packages";
import { Trust } from "@/components/Trust";
import { EnquiryForm } from "@/components/EnquiryForm";
import { FAQ } from "@/components/FAQ";
import { Footer } from "@/components/Footer";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";
import { WhoWeServe, HomeAbout } from "@/components/HomeExtras";
import { PageSeo } from "@/components/PageSeo";
import Admin from "@/pages/Admin";
import PropertyManagement from "@/pages/PropertyManagement";
import ParentCare from "@/pages/ParentCare";
import ServicesPage from "@/pages/ServicesPage";
import AboutPage from "@/pages/AboutPage";
import ContactPage from "@/pages/ContactPage";
import DynamicPage from "@/pages/DynamicPage";

const SECTION_COMPONENTS = {
  promise: Manifesto,
  serve: WhoWeServe,
  services: Services,
  how: HowItWorks,
  packages: Packages,
  trust: Trust,
  about_home: HomeAbout,
  enquiry: EnquiryForm,
  faq: FAQ,
};

const Site = () => {
  const { content } = useContent();

  useEffect(() => {
    if (window.location.hash) {
      const t = setTimeout(() => scrollTo(window.location.hash), 800);
      return () => clearTimeout(t);
    }
  }, []);

  const sections = content.homepage_sections || [];

  return (
  <div className="grain bg-[#FAF8F5]" data-testid="app-root">
    <PageSeo path="/" faqs={content.faq?.items} />
    <Navbar />
    <main>
      <Hero />
      <EditorialMarquee />
      {sections
        .filter((s) => s.enabled)
        .map((s) => {
          const C = SECTION_COMPONENTS[s.id];
          return C ? <C key={s.id} /> : null;
        })}
    </main>
    <Footer />
    <WhatsAppFloat />
  </div>
  );
};

const RedirectHandler = () => {
  const { content } = useContent();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const match = (content.redirects || []).find((r) => r.from === location.pathname);
    if (match) navigate(match.to, { replace: true });
  }, [location.pathname, content.redirects, navigate]);

  return null;
};

function App() {
  useEffect(() => {
    if (window.location.pathname.startsWith("/admin")) return;
    const lenis = new Lenis({ duration: 1.2, smoothWheel: true });
    window.__lenis = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <ContentProvider>
      <BrowserRouter>
        <RedirectHandler />
        <Routes>
          <Route path="/" element={<Site />} />
          <Route path="/nri-property-management" element={<PropertyManagement />} />
          <Route path="/nri-parent-care" element={<ParentCare />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/p/:slug" element={<DynamicPage />} />
          <Route path="/admin" element={<Admin />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </ContentProvider>
  );
}

export default App;
